import { Injectable, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationDTO } from '../models/notification.types';
import { NotificationApiService } from './notification-api.service';
import { WebsocketService } from './websocket.service';
import { AuthService } from '../auth/auth.service';
import { filter, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NotificationToastComponent } from '../../layout/common/notifications/notification-toast.component';

@Injectable({
    providedIn: 'root'
})
export class NotificationStore {
    private readonly _apiService = inject(NotificationApiService);
    private readonly _wsService = inject(WebsocketService);
    private readonly _authService = inject(AuthService);
    private readonly _snackBar = inject(MatSnackBar);
    private readonly destroyRef = inject(DestroyRef);

    // Signals
    private readonly _notifications = signal<NotificationDTO[]>([]);
    private readonly _unreadCount = signal<number>(0);
    private readonly _isLoading = signal<boolean>(false);
    private readonly _hasMore = signal<boolean>(true);
    private readonly _currentPage = signal<number>(0);

    // Public Signals
    public readonly notifications = this._notifications.asReadonly();
    public readonly unreadCount = this._unreadCount.asReadonly();
    public readonly isLoading = this._isLoading.asReadonly();
    public readonly hasMore = this._hasMore.asReadonly();

    // Derived state
    public readonly hasNotifications = computed(() => this._notifications().length > 0);

    private readonly PAGE_SIZE = 20;
    private initialized = false;
    private audioFile = new Audio('sounds/notification.mp3');

    constructor() {
        // Automatically fetch and connect when user logs in or session is restored
        this._authService.user$
            .pipe(takeUntilDestroyed())
            .subscribe(user => {
                const token = this._authService.accessToken;
                if (user && token) {
                    this.initialize(token);
                } else {
                    this.destroy();
                }
            });
    }

    /**
     * Initialize the store: fetch initial data and connect WebSocket
     */
    public initialize(token: string): void {
        if (this.initialized) return;
        this.initialized = true;

        this.fetchUnreadCount();
        this.loadFirstPage();

        // Connect STOMP client
        this._wsService.connect(token);

        // Subscribe to user notifications
        this._wsService.watch<NotificationDTO>('/user/queue/notifications')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((notification) => {
                this.handleIncomingNotification(notification);
            });
    }

    /**
     * Clean up when user logs out
     */
    public destroy(): void {
        this.initialized = false;
        this._wsService.disconnect();
        this._notifications.set([]);
        this._unreadCount.set(0);
        this._currentPage.set(0);
        this._hasMore.set(true);
    }

    private fetchUnreadCount(): void {
        this._apiService.getUnreadCount()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ count }) => this._unreadCount.set(count),
                error: (err) => console.error('Failed to fetch unread count:', err)
            });
    }

    public loadFirstPage(): void {
        this._isLoading.set(true);
        this._currentPage.set(0);

        this._apiService.getNotifications(0, this.PAGE_SIZE)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (items) => {
                    this._notifications.set(items);
                    this._hasMore.set(items.length === this.PAGE_SIZE);
                    this._isLoading.set(false);
                },
                error: (err) => {
                    console.error('Failed to load notifications:', err);
                    this._isLoading.set(false);
                }
            });
    }

    public loadMore(): void {
        if (this._isLoading() || !this._hasMore()) return;

        this._isLoading.set(true);
        const nextPage = this._currentPage() + 1;

        this._apiService.getNotifications(nextPage, this.PAGE_SIZE)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (newItems) => {
                    this._notifications.update(items => [...items, ...newItems]);
                    this._currentPage.set(nextPage);
                    this._hasMore.set(newItems.length === this.PAGE_SIZE);
                    this._isLoading.set(false);
                },
                error: (err) => {
                    console.error('Failed to load more notifications:', err);
                    this._isLoading.set(false);
                }
            });
    }

    private handleIncomingNotification(notification: NotificationDTO): void {
        // Prepend to list
        this._notifications.update(items => {
            // Check if it already exists (duplicate delivery)
            if (items.some(n => n.id === notification.id)) return items;
            return [notification, ...items];
        });

        // Increment badge
        this._unreadCount.update(count => count + 1);

        // Play sound
        this.playSound();

        // Show clickable toast
        this.showToast(notification);
    }

    public markAsRead(id: number): void {
        // Optimistic update
        this._notifications.update(items =>
            items.map(n => n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n)
        );

        this._unreadCount.update(count => Math.max(0, count - 1));

        // API call
        this._apiService.markAsRead(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: (err) => console.error('Failed to mark notification as read:', err)
            });
    }

    public markAllAsRead(): void {
        // Optimistic update
        this._notifications.update(items =>
            items.map(n => ({ ...n, read: true, readAt: n.readAt || new Date().toISOString() }))
        );
        this._unreadCount.set(0);

        // API call
        this._apiService.markAllAsRead()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: (err) => {
                    console.error('Failed to mark all as read:', err);
                    // Re-fetch count if it fails
                    this.fetchUnreadCount();
                }
            });
    }

    public deleteNotification(id: number): void {
        // Optimistic update
        const notification = this._notifications().find(n => n.id === id);
        const wasUnread = notification && !notification.read;

        this._notifications.update(items => items.filter(n => n.id !== id));
        if (wasUnread) {
            this._unreadCount.update(count => Math.max(0, count - 1));
        }

        // API call
        this._apiService.deleteNotification(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: (err) => console.error('Failed to delete notification:', err)
            });
    }

    private playSound(): void {
        this.audioFile.currentTime = 0;
        this.audioFile.play().catch(e => {
            // Autoplay policies might block this if user hasn't interacted with document
            console.warn('Could not play notification sound', e);
        });
    }

    private showToast(notification: NotificationDTO): void {
        this._snackBar.openFromComponent(NotificationToastComponent, {
            data: notification,
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            panelClass: ['notification-toast-panel']
        });
    }
}
